import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Mail, Phone, Linkedin, Languages, Briefcase, UserCircle, ExternalLink, Send, ArrowRight, Mic, Globe } from 'lucide-react';
import { api } from '../lib/api';
import { useTranslation } from '../i18nContext';
import BioContent from '../components/BioContent';

export default function InterpreterProfilePage() {
  const t = useTranslation();
  const { slug } = useParams();
  const [interpreter, setInterpreter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/interpreters/${slug}`).then(({ data }) => setInterpreter(data)).catch(() => setInterpreter(null)).finally(() => setLoading(false));
  }, [slug]);

  const directoryPath = `/${t.locale}/${t.routes.directory}`;
  // specialty is now list[str] (iter27); legacy data may still be a CSV string
  // OR an array containing a single CSV blob. Flatten + split by comma so each
  // specialty renders as its own chip (like languages).
  const rawSpec = interpreter?.specialty;
  const specialties = (Array.isArray(rawSpec) ? rawSpec : (typeof rawSpec === 'string' ? [rawSpec] : []))
    .flatMap((s) => String(s).split(','))
    .map((s) => s.trim())
    .filter(Boolean);
  const modalities = Array.isArray(interpreter?.modalities)
    ? interpreter.modalities.map((s) => String(s).trim()).filter(Boolean)
    : [];
  // language_pairs may be a list (new) or a comma-separated string (legacy).
  // It is NO LONGER the source-of-truth for the public combinations grid because
  // the bulk editor only edits `active_languages` + `passive_languages` and that
  // mismatch surfaced legacy/stale pairs to the public profile (iter53 bug).
  // The grid below is now derived from active/passive following AIIC conventions:
  //   • active × active = both directions (interpreter speaks both ways)
  //   • passive → active = one direction only (passive is only understood)
  const active = Array.isArray(interpreter?.active_languages) ? interpreter.active_languages.filter(Boolean) : [];
  const passive = Array.isArray(interpreter?.passive_languages) ? interpreter.passive_languages.filter(Boolean) : [];
  const derivedPairs = [];
  for (let i = 0; i < active.length; i += 1) {
    for (let j = 0; j < active.length; j += 1) {
      if (i !== j) derivedPairs.push([active[i], active[j]]);
    }
  }
  for (const p of passive) {
    for (const a of active) derivedPairs.push([p, a]);
  }
  // De-duplicate (in case data has Español listed twice) AND drop self-pairs
  // (Es→Es, En→En) which happen when an item appears in BOTH active+passive.
  // Also drop any pair the admin explicitly excluded for this interpreter.
  const excludedPairs = new Set(
    Array.isArray(interpreter?.excluded_language_pairs) ? interpreter.excluded_language_pairs : [],
  );
  const seenPairs = new Set();
  const pairs = derivedPairs.filter(([from, to]) => {
    if (!from || !to) return false;
    if (from === to) return false;
    if (excludedPairs.has(`${from}→${to}`)) return false;
    const k = `${from}|${to}`;
    if (seenPairs.has(k)) return false;
    seenPairs.add(k);
    return true;
  });
  const initials = (interpreter?.full_name || '').split(' ').slice(0, 2).map((n) => n.charAt(0)).join('');

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-20">
        <div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!interpreter) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 pt-32" data-testid="interpreter-not-found">
        <UserCircle size={64} className="text-charcoal-300 mb-4" />
        <h2 className="text-xl font-bold text-charcoal-700 mb-2">{t.directory.profile.notFound}</h2>
        <p className="text-charcoal-400 mb-6">{t.directory.profile.notFoundSub}</p>
        <Link to={directoryPath} className="inline-flex items-center gap-2 text-cmic-600 hover:text-cmic-700 font-medium">
          <ArrowLeft size={18} />
          {t.directory.profile.backToDirectory}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="interpreter-profile">
      <div className="bg-gradient-to-br from-cmic-800 via-cmic-700 to-cmic-600 pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-cmic-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={directoryPath} className="inline-flex items-center gap-2 text-cmic-200 hover:text-white transition-colors text-sm mb-8">
            <ArrowLeft size={16} />
            {t.directory.profile.backToDirectory}
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full">
            <path d="M0 60L60 55C120 50 240 40 360 35C480 30 600 30 720 32.5C840 35 960 40 1080 42.5C1200 45 1320 45 1380 45L1440 45V60H0Z" fill="#f9fafb" />
          </svg>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10 pb-20">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
              {interpreter.photo_url ? (
                <img src={interpreter.photo_url} alt={interpreter.full_name} className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover object-top shadow-lg flex-shrink-0" />
              ) : (
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-gradient-to-br from-cmic-500 to-cmic-700 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-white font-bold text-4xl sm:text-5xl">{initials}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 flex-wrap mb-4">
                  <h1 className="text-2xl sm:text-3xl font-bold text-charcoal-800">{interpreter.full_name}</h1>
                  {interpreter.membership_status && interpreter.membership_status !== 'active' && (
                    <MemberStatusBadge status={interpreter.membership_status} />
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mb-5">
                  <div className="flex items-center gap-2 text-charcoal-500 text-sm bg-gray-50 px-3 py-1.5 rounded-lg">
                    <MapPin size={15} className="text-cmic-500" />{interpreter.city}
                  </div>
                  {(!interpreter.active_languages?.length && !interpreter.passive_languages?.length) && interpreter.languages && (
                    <div className="flex items-center gap-2 text-charcoal-500 text-sm bg-gray-50 px-3 py-1.5 rounded-lg">
                      <Languages size={15} className="text-cmic-500" />{interpreter.languages}
                    </div>
                  )}
                </div>
                {specialties.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-cmic-600 mb-2 flex items-center gap-1.5">
                      <Briefcase size={13} /> Especialidades
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {specialties.map((spec) => (
                        <span key={spec} className="inline-flex items-center gap-1.5 text-xs font-medium bg-cmic-50 text-cmic-700 px-3 py-1.5 rounded-full border border-cmic-100">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {modalities.length > 0 && (
                  <div className="mb-6" data-testid="profile-modalities">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-violet-700 mb-2 flex items-center gap-1.5">
                      <Mic size={13} /> Modalidades de interpretación
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {modalities.map((m) => (
                        <span key={m} className="inline-flex items-center gap-1.5 text-xs font-medium bg-violet-50 text-violet-700 px-3 py-1.5 rounded-full border border-violet-100">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(interpreter.active_languages?.length > 0 || (interpreter.show_passive_chips && interpreter.passive_languages?.length > 0)) && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-2">
                    {interpreter.active_languages?.length > 0 && (
                      <div>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-cmic-600 mb-2 flex items-center gap-1.5">
                          <Languages size={13} /> Idiomas activos
                        </h3>
                        <div className="flex flex-wrap gap-1.5">
                          {interpreter.active_languages.map((l) => (
                            <span key={`a-${l}`} className="inline-flex items-center text-xs font-medium bg-cmic-100 text-cmic-700 px-2.5 py-1 rounded-full">
                              {l}
                            </span>
                          ))}
                        </div>
                        <p className="text-[10px] text-charcoal-400 mt-1.5">Interpreta hacia estos idiomas</p>
                      </div>
                    )}
                    {interpreter.show_passive_chips && interpreter.passive_languages?.length > 0 && (
                      <div>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-charcoal-500 mb-2 flex items-center gap-1.5">
                          <Languages size={13} /> Idiomas pasivos
                        </h3>
                        <div className="flex flex-wrap gap-1.5">
                          {interpreter.passive_languages.map((l) => (
                            <span key={`p-${l}`} className="inline-flex items-center text-xs font-semibold bg-amber-50 text-amber-800 px-2.5 py-1 rounded-full border border-amber-200">
                              {l}
                            </span>
                          ))}
                        </div>
                        <p className="text-[10px] text-charcoal-400 mt-1.5">Solo interpreta desde estos idiomas</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100">
            <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
              <div className="lg:col-span-2 p-8 sm:p-10 space-y-10 min-w-0">
                {pairs.length > 0 && (
                  <div>
                    <h2 className="text-lg font-bold text-charcoal-800 mb-4 flex items-center gap-2">
                      <Languages size={20} className="text-cmic-500" />
                      {t.directory.profile.languageCombinations}
                      <span className="text-xs font-normal text-charcoal-400 ml-1">· {pairs.length} {pairs.length === 1 ? 'combinación' : 'combinaciones'}</span>
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {pairs.map(([from, to], idx) => (
                        <div
                          key={`${from}-${to}-${idx}`}
                          data-testid="language-pair-card"
                          className="group flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-white via-cmic-50/40 to-white border border-cmic-100 hover:border-cmic-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex-1 min-w-0 text-right">
                            <span className="block text-[10px] uppercase tracking-wider text-cmic-500/80 font-semibold mb-0.5">Desde</span>
                            <span className="block font-semibold text-charcoal-800 truncate" title={from}>{from}</span>
                          </div>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cmic-500 text-white flex items-center justify-center group-hover:bg-cmic-600 transition-colors shadow-sm">
                            <ArrowRight size={14} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="block text-[10px] uppercase tracking-wider text-cmic-500/80 font-semibold mb-0.5">Hacia</span>
                            <span className="block font-semibold text-charcoal-800 truncate" title={to}>{to}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {interpreter.bio && (
                  <div>
                    <h2 className="text-lg font-bold text-charcoal-800 mb-4 flex items-center gap-2">
                      <UserCircle size={20} className="text-cmic-500" />
                      {t.directory.profile.about}
                    </h2>
                    <BioContent bio={interpreter.bio} className="text-charcoal-600 leading-relaxed text-[15px]" testid="interpreter-bio" />
                  </div>
                )}

                {interpreter.references_text && (() => {
                  const items = interpreter.references_text.split('\n').map((s) => s.trim()).filter(Boolean);
                  if (items.length === 0) return null;
                  const twoCols = items.length > 4;
                  return (
                    <div data-testid="profile-certifications">
                      <h2 className="text-lg font-bold text-charcoal-800 mb-4">{t.directory.profile.references}</h2>
                      <ol className={`grid gap-3 ${twoCols ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
                        {items.map((ref, i) => (
                          <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100 min-w-0">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cmic-100 text-cmic-700 text-sm font-bold flex items-center justify-center tabular-nums">{i + 1}</span>
                            <span className="text-charcoal-600 text-sm leading-relaxed break-words min-w-0">
                              {ref.startsWith('http') ? (
                                <a href={ref} target="_blank" rel="noopener noreferrer" className="text-cmic-600 hover:text-cmic-700 hover:underline inline-flex items-center gap-1 break-all">
                                  {ref}<ExternalLink size={12} />
                                </a>
                              ) : ref}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  );
                })()}
              </div>

              <div className="p-8 sm:p-10 bg-gray-50/50">
                <h2 className="text-lg font-bold text-charcoal-800 mb-6">{t.directory.profile.contactInfo}</h2>
                <div className="space-y-4">
                  {interpreter.email && (
                    <a href={`mailto:${interpreter.email}`} className="flex items-start gap-3 group">
                      <div className="w-10 h-10 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0 group-hover:bg-cmic-200 transition-colors">
                        <Mail size={18} className="text-cmic-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-charcoal-400 mb-0.5">Email</p>
                        <p className="text-sm text-cmic-600 group-hover:text-cmic-700 truncate transition-colors">{interpreter.email}</p>
                      </div>
                    </a>
                  )}
                  {(interpreter.additional_emails || []).filter(Boolean).map((em) => (
                    <a key={em} href={`mailto:${em}`} data-testid="profile-additional-email" className="flex items-start gap-3 group">
                      <div className="w-10 h-10 rounded-xl bg-cmic-50 flex items-center justify-center flex-shrink-0 group-hover:bg-cmic-100 transition-colors">
                        <Mail size={16} className="text-cmic-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-charcoal-400 mb-0.5">Email adicional</p>
                        <p className="text-sm text-cmic-600 group-hover:text-cmic-700 truncate transition-colors">{em}</p>
                      </div>
                    </a>
                  ))}
                  {interpreter.phone && (
                    <a href={`tel:${interpreter.phone}`} className="flex items-start gap-3 group">
                      <div className="w-10 h-10 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0 group-hover:bg-cmic-200 transition-colors">
                        <Phone size={18} className="text-cmic-600" />
                      </div>
                      <div>
                        <p className="text-xs text-charcoal-400 mb-0.5">{t.locale === 'es' ? 'Teléfono' : 'Phone'}</p>
                        <p className="text-sm text-charcoal-700">{interpreter.phone}</p>
                      </div>
                    </a>
                  )}
                  {(interpreter.additional_phones || []).filter(Boolean).map((ph) => (
                    <a key={ph} href={`tel:${ph}`} data-testid="profile-additional-phone" className="flex items-start gap-3 group">
                      <div className="w-10 h-10 rounded-xl bg-cmic-50 flex items-center justify-center flex-shrink-0 group-hover:bg-cmic-100 transition-colors">
                        <Phone size={16} className="text-cmic-500" />
                      </div>
                      <div>
                        <p className="text-xs text-charcoal-400 mb-0.5">{t.locale === 'es' ? 'Teléfono adicional' : 'Additional phone'}</p>
                        <p className="text-sm text-charcoal-700">{ph}</p>
                      </div>
                    </a>
                  ))}
                  {interpreter.city && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0">
                        <MapPin size={18} className="text-cmic-600" />
                      </div>
                      <div>
                        <p className="text-xs text-charcoal-400 mb-0.5">{t.locale === 'es' ? 'Ubicación' : 'Location'}</p>
                        <p className="text-sm text-charcoal-700">{interpreter.city}</p>
                      </div>
                    </div>
                  )}
                  {interpreter.linkedin_url && (
                    <a href={/^https?:\/\//i.test(interpreter.linkedin_url.trim()) ? interpreter.linkedin_url.trim() : `https://${interpreter.linkedin_url.trim()}`} target="_blank" rel="noopener noreferrer" data-testid="interpreter-linkedin-link" className="flex items-center gap-3 mt-6 bg-[#0077B5] hover:bg-[#005885] text-white rounded-xl px-4 py-3 transition-colors shadow-sm">
                      <Linkedin size={20} />
                      <span className="text-sm font-medium">{t.directory.profile.viewLinkedIn}</span>
                    </a>
                  )}
                  {interpreter.website && (
                    <a href={/^https?:\/\//i.test(interpreter.website.trim()) ? interpreter.website.trim() : `https://${interpreter.website.trim()}`} target="_blank" rel="noopener noreferrer" data-testid="interpreter-website-link" className="flex items-center gap-3 mt-3 bg-charcoal-700 hover:bg-charcoal-800 text-white rounded-xl px-4 py-3 transition-colors shadow-sm">
                      <Globe size={20} />
                      <span className="text-sm font-medium">{t.locale === 'es' ? 'Visitar página web' : 'Visit website'}</span>
                    </a>
                  )}
                  <Link
                    to={`/${t.locale}/${t.routes.contact}?interpreter=${interpreter.slug}&name=${encodeURIComponent(interpreter.full_name)}`}
                    data-testid="request-interpreter-btn"
                    className="flex items-center justify-center gap-2 mt-3 bg-gradient-to-r from-cmic-600 to-cmic-700 hover:from-cmic-700 hover:to-cmic-800 text-white rounded-xl px-4 py-3 transition-all shadow-sm hover:shadow-md font-medium text-sm"
                  >
                    <Send size={16} />
                    {t.locale === 'es' ? 'Solicitar este intérprete' : 'Request this interpreter'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


const STATUS_META = {
  candidate: { label: 'Candidato a miembro', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  senior:    { label: 'Miembro senior',      tone: 'bg-cmic-50 text-cmic-700 border-cmic-200' },
  honorary:  { label: 'Miembro honorario',   tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  associate: { label: 'Miembro asociado',    tone: 'bg-slate-50 text-slate-700 border-slate-200' },
};

function MemberStatusBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center text-[11px] font-semibold tracking-wide uppercase border rounded-full px-2.5 py-0.5 ${meta.tone}`}
      title={meta.label}
      data-testid="profile-status-badge"
    >
      {meta.label}
    </span>
  );
}
